/**
 * Data Bundle Modal Component
 * Modal for creating and editing data bundles - matches SkillModal design system
 */

import React, { useState, useEffect, useCallback } from 'react';
import { activeLocale } from '@/utils/locale';
import { message } from 'antd';
import { useLanguage } from '../../../hooks/useLanguage';
import { useFeature } from '@/hooks';
import { UploadArea, AgentPreview, ConfigGuideModal, GuideInfoButton, FilePreviewModal } from '../../../components/common';
import { ContextPromptViewer } from '../../../components/common/ContextPromptViewer';
import { DataBundleApi } from '../services/dataBundleService';
import { CloudFilePicker } from './CloudFilePicker';
import { CloudFolderLinker } from './CloudFolderLinker';
import { UrlResearchSection } from './UrlResearchSection';
import { VaultPickerModal } from '@modules/vault/components/VaultPickerModal';
import type { VaultPick } from '@modules/vault/components/VaultPickerModal';
import { vaultService } from '@modules/vault/vaultService';
import type { DataBundle, DataBundleFile, SyncFolder } from '../types';
import { KNOWLEDGE_ACCEPT } from '@/constants/knowledgeFileTypes';
import './DataBundleModal.css';

interface DataBundleModalProps {
  visible: boolean;
  bundle?: DataBundle;
  onSave: () => void;
  onCancel: () => void;
}

export const DataBundleModal: React.FC<DataBundleModalProps> = ({
  visible,
  bundle,
  onSave,
  onCancel,
}) => {
  const { t } = useLanguage();
  // Cloud file/folder pickers are a second entry point to cloud storage, so
  // they honor the same per-tenant "Cloud storage connections" switch as User
  // Settings — hidden (and the sub-modals not rendered) when it's off.
  const cloudEnabled = useFeature('integrations.cloud_storage');
  // "Add from the Library" needs a Library to add from — the vault API
  // refuses tenants without the flag, so the button hides with it.
  const libraryEnabled = useFeature('modules.library');
  const [loading, setLoading] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [formData, setFormData] = useState({
    bundle_name: '',
    bundle_description: '',
  });
  const [bundleFiles, setBundleFiles] = useState<DataBundleFile[]>([]);
  const [syncFolders, setSyncFolders] = useState<SyncFolder[]>([]);
  const [uploading, setUploading] = useState(false);
  // "Add from the Library": pick a folder or a file already filed in this
  // organization and put it in the bundle. A folder contributes the files
  // directly inside it — a bundle is flat, so pretending otherwise would
  // silently drop everything nested.
  const [libraryPickerOpen, setLibraryPickerOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [cloudPickerVisible, setCloudPickerVisible] = useState(false);
  const [folderLinkerVisible, setFolderLinkerVisible] = useState(false);
  const [contextPrompt, setContextPrompt] = useState('');
  const [regeneratingBundlePrompt, setRegeneratingBundlePrompt] = useState(false);
  // Removed generatedPreview state — auto-file-sync replaces the
  // preview-then-apply flow for the bundle context prompt.
  const [expandedSourcesFileId, setExpandedSourcesFileId] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<{ fileId?: string; filePath?: string; fileName: string; fileType: string } | null>(null);

  useEffect(() => {
    if (visible && bundle) {
      // Edit mode - fetch full bundle data
      setFormData({
        bundle_name: bundle.bundle_name,
        bundle_description: bundle.bundle_description || '',
      });
      setSyncFolders(bundle.sync_folders || []);
      setBundleFiles([]);

      DataBundleApi.getBundle(bundle.data_bundle_id)
        .then((fullBundle) => {
          setFormData({
            bundle_name: fullBundle.bundle_name,
            bundle_description: fullBundle.bundle_description || '',
          });
          setSyncFolders(fullBundle.sync_folders || []);
          setBundleFiles(fullBundle.files || []);
          setContextPrompt(fullBundle.bundle_context_prompt || '');
        })
        .catch((err) => console.error('Error loading bundle details:', err));
    } else if (visible) {
      // Create mode - reset form
      setFormData({
        bundle_name: '',
        bundle_description: '',
      });
      setBundleFiles([]);
      setSyncFolders([]);
      setContextPrompt('');
    }
  }, [visible, bundle]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.bundle_name.trim()) {
      message.warning(t('dataBundles.enterBundleName'));
      return;
    }

    setLoading(true);
    try {
      if (bundle) {
        const updateData: any = {
          bundle_name: formData.bundle_name,
          bundle_description: formData.bundle_description || undefined,
          bundle_context_prompt: contextPrompt || null,
        };
        await DataBundleApi.updateBundle(bundle.data_bundle_id, updateData);
      } else {
        await DataBundleApi.createBundle({
          bundle_name: formData.bundle_name,
          bundle_description: formData.bundle_description || undefined,
        });
      }

      onSave();
    } catch (error: any) {
      console.error('Error saving bundle:', error);
      message.error(bundle ? t('dataBundles.updateFailed') : t('dataBundles.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  // After a file mutation, the cascade regenerates the bundle's
  // bundle_context_prompt on a debounced timer. Poll the server six
  // times across the realistic regen window so the inline
  // ContextPromptViewer updates without closing the modal.
  const scheduleBundleContextRefresh = useCallback(() => {
    if (!bundle) return;
    const refresh = async () => {
      try {
        const fresh = await DataBundleApi.getBundle(bundle.data_bundle_id);
        setContextPrompt(fresh.bundle_context_prompt || '');
      } catch (err) {
        console.error('[DataBundleModal] context refresh failed:', err);
      }
    };
    [3_000, 5_000, 8_000, 12_000, 18_000, 25_000].forEach(ms =>
      setTimeout(refresh, ms),
    );
  }, [bundle]);

  // File upload handlers
  const handleFileUpload = useCallback(
    async (files: FileList | File[]) => {
      if (!bundle) return; // Can only upload files in edit mode

      setUploading(true);
      try {
        for (const file of Array.from(files)) {
          const bundleFile = await DataBundleApi.uploadFile(bundle.data_bundle_id, file);
          setBundleFiles((prev) => [...prev, bundleFile]);
        }
        message.success(t('dataBundles.filesUploaded'));
        scheduleBundleContextRefresh();
      } catch (error: any) {
        console.error('Error uploading file:', error);
        const msg = error?.response?.data?.error || t('skills.uploadFailed');
        message.error(msg);
      } finally {
        setUploading(false);
      }
    },
    [bundle, scheduleBundleContextRefresh]
  );

  const handleFileDelete = async (fileId: string) => {
    if (!bundle) return;

    try {
      await DataBundleApi.removeFile(bundle.data_bundle_id, fileId);
      setBundleFiles((prev) => prev.filter((f) => f.file_id !== fileId));
      message.success(t('dataBundles.fileRemoved'));
      scheduleBundleContextRefresh();
    } catch (error: any) {
      console.error('Error deleting file:', error);
      message.error(t('dataBundles.removeFileFailed'));
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  // Cloud file picker handler
  const handleCloudFilesSelected = async (provider: string, fileIds: string[]) => {
    if (!bundle) return;

    setCloudPickerVisible(false);
    setUploading(true);
    try {
      const result = await DataBundleApi.addCloudFiles(bundle.data_bundle_id, {
        provider,
        file_ids: fileIds,
      });
      setBundleFiles((prev) => [...prev, ...result.bundle_files]);
      if (result.bundle_files.length > 0) {
        message.success(t('counts.cloudFilesAdded', { count: result.bundle_files.length }));
        scheduleBundleContextRefresh();
      }
      if (result.skipped.length > 0) {
        message.warning(t('counts.filesSkipped', { count: result.skipped.length, reasons: result.skipped.map(s => s.reason).join(', ') }));
      }
      if (result.bundle_files.length === 0 && result.skipped.length === 0) {
        message.info(t('dataBundles.noNewFiles'));
      }
    } catch (error: any) {
      console.error('Error adding cloud files:', error);
      message.error(t('dataBundles.addCloudFilesFailed'));
    } finally {
      setUploading(false);
    }
  };

  // Sync folder handlers
  const handleFolderLinked = async (
    provider: string,
    folderId: string,
    folderName: string,
    folderPath?: string
  ) => {
    if (!bundle) return;

    setFolderLinkerVisible(false);
    try {
      const updatedBundle = await DataBundleApi.addSyncFolder(bundle.data_bundle_id, {
        provider,
        folder_id: folderId,
        folder_name: folderName,
        folder_path: folderPath,
      });
      setSyncFolders(updatedBundle.sync_folders || []);
      message.success(t('dataBundles.syncFolderLinked'));
      scheduleBundleContextRefresh();
    } catch (error: any) {
      console.error('Error linking sync folder:', error);
      const msg = error?.response?.data?.error || t('dataBundles.failedToLinkFolder');
      message.error(msg);
    }
  };

  const handleRemoveSyncFolder = async (folderId: string) => {
    if (!bundle) return;

    try {
      const updatedBundle = await DataBundleApi.removeSyncFolder(bundle.data_bundle_id, folderId);
      setSyncFolders(updatedBundle.sync_folders || []);
      message.success(t('dataBundles.syncFolderRemoved'));
      scheduleBundleContextRefresh();
    } catch (error: any) {
      console.error('Error removing sync folder:', error);
      message.error(t('dataBundles.removeSyncFolderFailed'));
    }
  };

  const handleSync = async () => {
    if (!bundle) return;

    setSyncing(true);
    try {
      const stats = await DataBundleApi.syncBundle(bundle.data_bundle_id);
      if (stats.errors?.length) {
        message.warning(stats.errors[0], 6);
      } else {
        message.success(
          `Sync complete: ${stats.added} added, ${stats.updated} updated, ${stats.removed} removed`
        );
      }
      // Reload bundle data to get updated files
      const fullBundle = await DataBundleApi.getBundle(bundle.data_bundle_id);
      setBundleFiles(fullBundle.files || []);
      setSyncFolders(fullBundle.sync_folders || []);
      // Manual sync may have added/updated/removed files — schedule a
      // refresh so the context prompt picks up the cascade's regen.
      if ((stats.added || 0) + (stats.updated || 0) + (stats.removed || 0) > 0) {
        scheduleBundleContextRefresh();
      }
    } catch (error: any) {
      console.error('Error syncing bundle:', error);
      const msg = error?.response?.data?.error || t('dataBundles.failedToSync');
      message.error(msg);
    } finally {
      setSyncing(false);
    }
  };

  const getFileIcon = (fileType?: string): string => {
    if (fileType?.startsWith('image/')) return 'image';
    if (fileType?.includes('pdf')) return 'pdf';
    if (fileType?.includes('spreadsheet') || fileType?.includes('excel')) return 'sheet';
    return 'doc';
  };

  const getSourceIcon = (sourceType: string) => {
    if (sourceType === 'cloud_file' || sourceType === 'cloud_sync') {
      return (
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
        </svg>
      );
    }
    return null;
  };

  if (!visible) return null;

  const isEditMode = !!bundle;

  return (
    <div className="modal-overlay">
      <div
        className="modal-container bundle-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="modal-header">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <h2 className="modal-title">
                {bundle ? t('dataBundles.editBundleTitle') : t('dataBundles.createBundleTitle')}
              </h2>
              <GuideInfoButton onClick={() => setShowGuide(true)} />
            </div>
            <p className="modal-subtitle">
              {t('dataBundles.organizeFilesAndCloudFolders')}
            </p>
          </div>
          {/* Right-side controls — wrapped so flex `space-between` on the
              modal-header doesn't leave a gap between refresh and close
              when the title is short. */}
          <div style={{ display: 'flex', alignItems: 'center', marginInlineStart: 'auto', gap: '8px' }}>
            {bundle && (
              // Refresh button — same shape, style, and behaviour as the
              // top-right refresh on the BotModal edit screen. Forces a
              // fresh Haiku summary of this bundle from its current files
              // and pulls the new prompt back into the inline viewer.
              <button
                type="button"
                style={{
                  background: 'none',
                  border: '1px solid var(--border-color, #d0d5dd)',
                  borderRadius: '5px',
                  padding: '4px 8px',
                  cursor: regeneratingBundlePrompt ? 'wait' : 'pointer',
                  color: 'var(--text-secondary, #667085)',
                  display: 'flex',
                  alignItems: 'center',
                  opacity: regeneratingBundlePrompt ? 0.5 : 1,
                }}
                disabled={regeneratingBundlePrompt}
                title={t('dataBundles.reSummarize')}
                onClick={async () => {
                  try {
                    setRegeneratingBundlePrompt(true);
                    await DataBundleApi.generateContextPrompt(bundle.data_bundle_id);
                    const fresh = await DataBundleApi.getBundle(bundle.data_bundle_id);
                    setContextPrompt(fresh.bundle_context_prompt || '');
                  } catch (err) {
                    console.error('[DataBundleModal] regenerate failed', err);
                  } finally {
                    setRegeneratingBundlePrompt(false);
                  }
                }}
              >
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" className={regeneratingBundlePrompt ? 'spinning' : ''}>
                  <path d="M1 4v6h6" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                </svg>
              </button>
            )}
            <button
              type="button"
              className="modal-close"
              onClick={onCancel}
              aria-label={t('common.close')}
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="modal-body">
          {/* Bundle Name */}
          <div className="form-group">
            <label className="form-label">
              {t('dataBundles.bundleNameLabel')} <span className="required">*</span>
            </label>
            <input dir="auto"
              type="text"
              className="form-input"
              placeholder="e.g. Product Knowledge Base"
              value={formData.bundle_name}
              onChange={(e) =>
                setFormData({ ...formData, bundle_name: e.target.value })
              }
              disabled={loading}
            />
          </div>

          {/* Description */}
          <div className="form-group">
            <label className="form-label">{t('skills.description')}</label>
            <input dir="auto"
              type="text"
              className="form-input"
              placeholder={t('dataBundles.descriptionPlaceholder')}
              value={formData.bundle_description}
              onChange={(e) =>
                setFormData({ ...formData, bundle_description: e.target.value })
              }
              disabled={loading}
            />
          </div>

          {/* Files Section */}
          <div className="data-bundle-section">
            <label className="section-label">{t('dataBundles.files')}</label>
            <p className="section-description">
              {t('dataBundles.uploadFilesOrAddThem')}
            </p>

            {/* File List */}
            {bundleFiles.length > 0 && (
              <div className="bundle-files-list">
                {bundleFiles.map((file) => (
                  <div key={file.bundle_file_id} className="bundle-file-item">
                    <div
                      className="bundle-file-clickable"
                      onClick={() => setPreviewFile({
                        fileId: file.file_id,
                        filePath: file.file_path,
                        fileName: file.file_display_name || file.file_name || 'Unknown',
                        fileType: file.file_type || '',
                      })}
                      style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0, cursor: 'pointer' }}
                    >
                    <div className="bundle-file-icon">
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                        {getFileIcon(file.file_type) === 'pdf' ? (
                          <>
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                          </>
                        ) : getFileIcon(file.file_type) === 'image' ? (
                          <>
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <polyline points="21 15 16 10 5 21" />
                          </>
                        ) : (
                          <>
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <line x1="16" y1="13" x2="8" y2="13" />
                            <line x1="16" y1="17" x2="8" y2="17" />
                          </>
                        )}
                      </svg>
                    </div>
                    <div className="bundle-file-info">
                      <span className="bundle-file-name" dir="auto">
                        {file.file_display_name || file.file_name}
                      </span>
                      {file.source_url && (
                        <span className="bundle-file-web-links">
                          <a
                            href={file.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bundle-file-web-link"
                            title={`Open ${file.source_url}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                              <polyline points="15 3 21 3 21 9" />
                              <line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                            {(() => { try { return new URL(file.source_url).hostname; } catch { return 'source'; } })()}
                          </a>
                          {file.crawled_sources && file.crawled_sources.length > 1 && (
                            <button
                              type="button"
                              className="bundle-file-sources-toggle"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedSourcesFileId(
                                  expandedSourcesFileId === file.bundle_file_id ? null : file.bundle_file_id
                                );
                              }}
                            >
                              {file.crawled_sources.length} pages
                              <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2"
                                style={{ transform: expandedSourcesFileId === file.bundle_file_id ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>
                                <polyline points="9 18 15 12 9 6" />
                              </svg>
                            </button>
                          )}
                          {expandedSourcesFileId === file.bundle_file_id && file.crawled_sources && (
                            <div className="bundle-file-sources-dropdown">
                              {file.crawled_sources.map((src, i) => (
                                <a key={i} href={src} target="_blank" rel="noopener noreferrer" className="bundle-file-source-item">
                                  <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                    <polyline points="15 3 21 3 21 9" />
                                    <line x1="10" y1="14" x2="21" y2="3" />
                                  </svg>
                                  {src}
                                </a>
                              ))}
                            </div>
                          )}
                        </span>
                      )}
                      <span className="bundle-file-meta">
                        {file.file_size_mb != null && (
                          <span className="bundle-file-size">
                            {file.file_size_mb.toFixed(2)} MB
                          </span>
                        )}
                        {file.source_type !== 'upload' && (
                          <span className="bundle-file-source">
                            {getSourceIcon(file.source_type)}
                            {file.cloud_provider}
                          </span>
                        )}
                      </span>
                    </div>
                    </div>
                    <button
                      type="button"
                      className="bundle-file-delete"
                      onClick={() => handleFileDelete(file.file_id)}
                      title={t('dataBundles.removeFile')}
                    >
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload Area */}
            {isEditMode ? (
              <>
                <UploadArea
                  uploading={uploading}
                  dragOver={dragOver}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onFileSelect={(files) => handleFileUpload(files)}
                  accept={KNOWLEDGE_ACCEPT}
                />

                {/* Cloud Actions */}
                <div className="bundle-cloud-actions">
                  {libraryEnabled && (
                    <button
                      type="button"
                      className="btn-cloud-action"
                      onClick={() => setLibraryPickerOpen(true)}
                      disabled={uploading}
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                      </svg>
                      {t('vault.attach.add')}
                    </button>
                  )}
                  {cloudEnabled && (
                    <button
                      type="button"
                      className="btn-cloud-action"
                      onClick={() => setCloudPickerVisible(true)}
                      disabled={uploading}
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
                      </svg>
                      {t('dataBundles.addFromCloud')}
                    </button>
                  )}
                  <UrlResearchSection
                    mode="bundle"
                    bundleId={bundle.data_bundle_id}
                    onFileSaved={(file) => setBundleFiles((prev) => [...prev, file])}
                  />
                </div>
              </>
            ) : (
              <div className="placeholder-area">
                <p>{t('dataBundles.saveBundleFirstForFiles')}</p>
              </div>
            )}
          </div>

          {/* "Add from the Library" — the picker plus what happens to the pick.
              A file joins the bundle as itself; a folder contributes the files
              directly in it, and says how many, because a bundle cannot hold a
              folder and quietly adding nothing would look like a broken button. */}
          <VaultPickerModal
            open={libraryPickerOpen}
            onCancel={() => setLibraryPickerOpen(false)}
            onPick={async (pick: VaultPick) => {
              if (!bundle?.data_bundle_id) return;
              try {
                let fileIds: string[] = [];
                if (pick.file_id) {
                  fileIds = [pick.file_id];
                } else if (pick.folder_id) {
                  const folder = await vaultService.getFolder(pick.folder_id);
                  fileIds = (folder.files || []).map((f) => f.file_id);
                  if (fileIds.length === 0) {
                    message.info(t('bundles.library.folderEmpty', { name: pick.name }));
                    return;
                  }
                }
                const result = await DataBundleApi.attachExistingFiles(
                  bundle.data_bundle_id, fileIds,
                );
                setBundleFiles((prev) => [...prev, ...result.added]);
                if (result.added.length) {
                  message.success(t('bundles.library.added', { n: result.added.length }));
                }
                // Say what did NOT land, rather than leaving a person to count.
                if (result.skipped.length) {
                  message.info(t('bundles.library.skipped', { n: result.skipped.length }));
                }
                setLibraryPickerOpen(false);
              } catch (e: any) {
                message.error(e?.response?.data?.error || t('bundles.library.failed'));
              }
            }}
          />

          {/* Sync Folders Section — cloud-storage surface, gated on the
              per-tenant "Cloud storage connections" switch. Folders can be
              linked and refreshed MANUALLY (Sync Now). Timed auto-sync is
              intentionally NOT offered — the background scheduler is disabled
              (DATA_BUNDLE_AUTO_SYNC_ENABLED) after a runaway re-sync incident. */}
          {cloudEnabled && (
          <div className="data-bundle-section">
            <div className="section-header-row">
              <div>
                <label className="section-label">{t('dataBundles.syncFolders')}</label>
                <p className="section-description">
                  {t('dataBundles.linkCloudFoldersAndRefresh')}
                </p>
              </div>
              {isEditMode && syncFolders.length > 0 && (
                <button
                  type="button"
                  className="btn-sync"
                  onClick={handleSync}
                  disabled={syncing || syncFolders.every(f => f.sync_status === 'dormant')}
                  title={syncFolders.every(f => f.sync_status === 'dormant') ? t('dataBundles.foldersOtherAccount') : undefined}
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={syncing ? 'spin' : ''}
                  >
                    <polyline points="23 4 23 10 17 10" />
                    <polyline points="1 20 1 14 7 14" />
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                  </svg>
                  {syncing ? t('common.syncing') : t('dataBundles.syncNow')}
                </button>
              )}
            </div>

            {/* Sync Folder List */}
            {syncFolders.length > 0 && (
              <div className="sync-folders-list">
                {syncFolders.map((folder) => {
                  const isDormant = folder.sync_status === 'dormant';
                  return (
                    <div key={folder.folder_id} className={`sync-folder-item${isDormant ? ' sync-folder-dormant' : ''}`}>
                      <div className="sync-folder-icon">
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                        </svg>
                      </div>
                      <div className="sync-folder-info">
                        <span dir="auto" className="sync-folder-name">
                          {folder.folder_name}
                          {isDormant && (
                            <span className="sync-folder-status-badge dormant" title={t('dataBundles.folderDifferentAccount')}>
                              dormant
                            </span>
                          )}
                        </span>
                        <span className="sync-folder-meta">
                          <span className="sync-folder-provider">{folder.provider}</span>
                          {folder.folder_path && (
                            <span className="sync-folder-path">{folder.folder_path}</span>
                          )}
                          {isDormant ? (
                            <span className="sync-folder-dormant-hint">
                              {t('dataBundles.differentAccountConnected')}
                            </span>
                          ) : folder.last_sync_at ? (
                            <span className="sync-folder-last-sync">
                              {t('dataBundles.lastSync', { when: new Date(folder.last_sync_at).toLocaleString(activeLocale()) })}
                            </span>
                          ) : null}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="sync-folder-remove"
                        onClick={() => handleRemoveSyncFolder(folder.folder_id)}
                        title={t('dataBundles.removeSyncFolder')}
                      >
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {isEditMode ? (
              <button
                type="button"
                className="btn-link-folder"
                onClick={() => setFolderLinkerVisible(true)}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                {t('dataBundles.linkSyncFolder')}
              </button>
            ) : (
              <div className="placeholder-area">
                <p>{t('dataBundles.saveBundleFirstForFolders')}</p>
              </div>
            )}
          </div>
          )}

          {/* Bundle Context Prompt — auto-syncs on every file
              change (see auto-file-sync feature). Read-only here.
              No wrapping section box — matches the minimal look used
              in BotModal / TenantContextEditor. */}
          {bundle && (
            <ContextPromptViewer
              prompt={contextPrompt}
              label={t('dataBundles.bundleContextPrompt')}
              defaultOpen
            />
          )}

          {/* Agent Preview — at bottom. Refresh moved to the top-right
              modal-header button (same position/style as BotModal). */}
          {bundle && (
            <AgentPreview
              title={t('dataBundles.bundlePreview')}
              subtitle={t('dataBundles.contextPromptSubtitle')}
              content={(() => {
                if (contextPrompt) {
                  return `### ${formData.bundle_name || 'Bundle'}\n${contextPrompt}`;
                }
                if (bundleFiles.length > 0) {
                  const fileNames = bundleFiles.map(f => f.file_name || f.file_display_name).join(', ');
                  return `- **${formData.bundle_name || 'Bundle'}** (${bundleFiles.length} files): ${fileNames}`;
                }
                return '';
              })()}
            />
          )}
        </form>

        {/* Modal Footer */}
        <div className="modal-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={onCancel}
            disabled={loading}
          >
            {t('vault.cancel')}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Saving...' : bundle ? t('dataBundles.saveBundle') : t('dataBundles.createBundle')}
          </button>
        </div>
      </div>

      {/* Cloud sub-modals — only mounted when cloud storage is enabled for the
          tenant (their trigger buttons are already gated the same way). */}
      {cloudEnabled && (
        <>
          <CloudFilePicker
            visible={cloudPickerVisible}
            onSelect={handleCloudFilesSelected}
            onCancel={() => setCloudPickerVisible(false)}
          />
          <CloudFolderLinker
            visible={folderLinkerVisible}
            onSelect={handleFolderLinked}
            onCancel={() => setFolderLinkerVisible(false)}
          />
        </>
      )}

      <ConfigGuideModal section="data-bundle" visible={showGuide} onClose={() => setShowGuide(false)} />

      <FilePreviewModal
        visible={!!previewFile}
        fileId={previewFile?.fileId}
        filePath={previewFile?.filePath}
        fileName={previewFile?.fileName || ''}
        fileType={previewFile?.fileType}
        onClose={() => setPreviewFile(null)}
      />
    </div>
  );
};
